/**
 * FAQ Management Service (Admin)
 *
 * Provides methods for system administrators to manage FAQ categories and questions
 */

import api from '../api';
import type {
  FaqCategory,
  FaqQuestion,
  CreateCategoryData,
  UpdateCategoryData,
  CreateQuestionData,
  UpdateQuestionData,
  FaqCategoryResponse,
  FaqQuestionResponse,
  FaqQuestionsListResponse,
  FaqCreateUpdateResponse,
  FaqDeleteResponse,
} from '../../types/faq';

// ===== Category Management =====

/**
 * Get all FAQ categories
 */
export async function getAllCategories(): Promise<FaqCategory[]> {
  const response = await api.get<{ categories: FaqCategory[] }>('/api/admin/faq/categories');
  return response.data.categories;
}

/**
 * Get single FAQ category by ID
 */
export async function getCategoryById(id: number): Promise<FaqCategory> {
  const response = await api.get<FaqCategoryResponse>(`/api/admin/faq/categories/${id}`);
  return response.data.category;
}

/**
 * Create new FAQ category
 */
export async function createCategory(data: CreateCategoryData): Promise<FaqCategory> {
  const response = await api.post<FaqCreateUpdateResponse>('/api/admin/faq/categories', data);
  return response.data.category!;
}

/**
 * Update existing FAQ category
 */
export async function updateCategory(
  id: number,
  data: UpdateCategoryData
): Promise<FaqCategory> {
  const response = await api.put<FaqCreateUpdateResponse>(
    `/api/admin/faq/categories/${id}`,
    data
  );
  return response.data.category!;
}

/**
 * Delete FAQ category (cascades to questions)
 */
export async function deleteCategory(id: number): Promise<FaqDeleteResponse> {
  const response = await api.delete<FaqDeleteResponse>(`/api/admin/faq/categories/${id}`);
  return response.data;
}

// ===== Question Management =====

export interface QuestionListParams {
  category_id?: number;
  status?: 'draft' | 'published';
  search?: string;
  page?: number;
}

/**
 * Get paginated list of FAQ questions with filters
 */
export async function getAllQuestions(
  params?: QuestionListParams
): Promise<FaqQuestionsListResponse> {
  const response = await api.get<FaqQuestionsListResponse>('/api/admin/faq/questions', {
    params,
  });
  return response.data;
}

/**
 * Get single FAQ question by ID
 */
export async function getQuestionById(id: number): Promise<FaqQuestion> {
  const response = await api.get<FaqQuestionResponse>(`/api/admin/faq/questions/${id}`);
  return response.data.question;
}

/**
 * Create new FAQ question
 */
export async function createQuestion(data: CreateQuestionData): Promise<FaqQuestion> {
  const response = await api.post<FaqCreateUpdateResponse>('/api/admin/faq/questions', data);
  return response.data.question!;
}

/**
 * Update existing FAQ question
 */
export async function updateQuestion(
  id: number,
  data: UpdateQuestionData
): Promise<FaqQuestion> {
  const response = await api.put<FaqCreateUpdateResponse>(
    `/api/admin/faq/questions/${id}`,
    data
  );
  return response.data.question!;
}

/**
 * Delete FAQ question
 */
export async function deleteQuestion(id: number): Promise<FaqDeleteResponse> {
  const response = await api.delete<FaqDeleteResponse>(`/api/admin/faq/questions/${id}`);
  return response.data;
}

/**
 * Category management service object
 */
export const faqCategoryAdminService = {
  getAllCategories,
  getCategoryById,
  createCategory,
  updateCategory,
  deleteCategory,
};

/**
 * Question management service object
 */
export const faqQuestionAdminService = {
  getAllQuestions,
  getQuestionById,
  createQuestion,
  updateQuestion,
  deleteQuestion,
};
